import { settingI18nKey, type CatalogSetting, type SettingValue } from '@nexus/core';
import { Badge, Button, SettingsRow, toast } from '@nexus/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { adminSettingsQuery } from '../admin/api';
import { useAdminError } from '../admin/shared';
import { api } from '../api/client';
import { settingsQuery, useSettings, useUpdatePersonalSetting } from '../session/settings';
import { SettingControl } from './setting-control';
import { useNavigationTargets } from './targets';

/** Segmented controls label themselves, every other control gets the row label. */
function controlId(setting: CatalogSetting, id: string): string | undefined {
  return setting.type === 'select' && setting.options.length <= 3 ? undefined : id;
}

/** A personal setting with its current value, marked and resettable once the person changed it. */
export function PersonalSettingRow({
  setting,
  description,
}: {
  setting: CatalogSetting;
  /** Replaces the description of the setting, e.g. with the address a channel sends to. */
  description?: string;
}) {
  const { t } = useTranslation();
  const settings = useSettings();
  const update = useUpdatePersonalSetting();
  const targets = useNavigationTargets();
  const id = `setting-${setting.key}`;
  const personal = settings.personal.includes(setting.key);

  function change(value: SettingValue | null) {
    update.mutate(
      { key: setting.key, value },
      { onError: () => toast.error(t('common.saveFailed')) },
    );
  }

  return (
    <SettingsRow
      htmlFor={controlId(setting, id)}
      label={t(`${settingI18nKey(setting)}.label`)}
      description={description ?? t(`${settingI18nKey(setting)}.description`)}
      badge={personal ? <Badge>{t('profile.changed')}</Badge> : null}
    >
      <SettingControl
        id={id}
        setting={setting}
        value={settings.values[setting.key] ?? setting.default}
        targets={targets}
        onChange={change}
      />
      {personal && (
        <Button size="sm" variant="ghost" icon="rotate-ccw" onClick={() => change(null)}>
          {t('profile.resetToDefault')}
        </Button>
      )}
    </SettingsRow>
  );
}

/** Saves an admin value; `null` removes it. Company defaults change everyone's values. */
export function useSaveAdminSetting() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const reportError = useAdminError();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: SettingValue | null }) =>
      api.put(`/settings/admin/${encodeURIComponent(key)}`, { value }),
    onSuccess: () => toast.success(t('admin.settings.saved')),
    onError: (error) => reportError(error),
    onSettled: async () => {
      await client.invalidateQueries(adminSettingsQuery);
      await client.invalidateQueries(settingsQuery);
    },
  });
}

/** An admin value: a company setting, a global one, or the company default of a personal one. */
export function AdminSettingRow({
  setting,
  stored,
  onChange,
}: {
  setting: CatalogSetting;
  /** The value stored at this level, undefined while the default applies. */
  stored: SettingValue | undefined;
  onChange: (value: SettingValue | null) => void;
}) {
  const { t } = useTranslation();
  const targets = useNavigationTargets();
  const id = `admin-setting-${setting.key}`;
  return (
    <SettingsRow
      htmlFor={controlId(setting, id)}
      label={t(`${settingI18nKey(setting)}.label`)}
      description={t(`${settingI18nKey(setting)}.description`)}
      badge={
        stored === undefined ? null : (
          <Badge tone="brand">{t('admin.settings.companyDefault')}</Badge>
        )
      }
    >
      <SettingControl
        id={id}
        setting={setting}
        value={stored ?? setting.default}
        targets={targets}
        onChange={onChange}
      />
      {stored !== undefined && (
        <Button size="sm" variant="ghost" icon="rotate-ccw" onClick={() => onChange(null)}>
          {t('admin.settings.removeDefault')}
        </Button>
      )}
    </SettingsRow>
  );
}
